import ConfirmModal from '@/components/confirm-modal'
import { NumberSearchHit, searchByNumber } from '@/lib/api/global-search/global-search-api'
import {
    linkOrphanMany,
    OrphanAppointmentType,
    OrphanLinkManyAppointment,
    OrphanLinkManyPreview,
    OrphanPaymentWithSuggestions,
    previewOrphanLinkMany,
} from '@/lib/api/financial/orphan-payments-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useEffect, useMemo, useState } from 'react'
import { MdAdd, MdClose, MdDeleteOutline, MdErrorOutline, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso)) : '-'

const APPOINTMENT_TYPES: OrphanAppointmentType[] = ['EXAM', 'CONSULTATION', 'PROCEDURE']

const keyOf = (a: { appointmentType: string; appointmentId: number }) => `${a.appointmentType}:${a.appointmentId}`

interface Props {
    orphan: OrphanPaymentWithSuggestions
    typeLabel: Record<OrphanAppointmentType, string>
    onClose: () => void
    onLinked: (message: string) => void
}

/**
 * Pix único que pagou VÁRIOS atendimentos: a família que paga de uma vez, a empresa que
 * sorteou consultas entre os funcionários. O Pix vira a cobrança de todos, com a parte de
 * cada um, e o repasse sai sozinho.
 *
 * Quem decide o que entra é o backend (`link-many/preview`): só atendimento sem cobrança
 * viva e sem nada recebido, todos da mesma filial, e a soma batendo com o Pix. Um único
 * atendimento travado recusa a operação inteira, e a tela mostra o motivo nele.
 *
 * Consulta zerada por cupom pode ter sido paga por terceiro: marcada, ela entra pelo valor
 * antes do cupom, o cupom continua registrado e quem pagou fica gravado na cobrança.
 */
export default function LinkManyModal({ orphan, typeLabel, onClose, onLinked }: Props) {
    const { ClientLabel } = useClientLabel()

    const [selected, setSelected] = useState<OrphanLinkManyAppointment[]>([])
    const [payer, setPayer] = useState('')
    const [preview, setPreview] = useState<OrphanLinkManyPreview | null>(null)
    const [previewing, setPreviewing] = useState(false)
    const [term, setTerm] = useState('')
    const [searching, setSearching] = useState(false)
    const [searched, setSearched] = useState(false)
    const [hits, setHits] = useState<NumberSearchHit[]>([])
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [linking, setLinking] = useState(false)

    const selectedKeys = useMemo(() => new Set(selected.map(keyOf)), [selected])
    const anyThirdParty = selected.some(a => a.thirdPartyCovered)

    // A simulação acompanha cada mudança; o nome de quem pagou espera a digitação parar.
    // `previewing` liga JÁ na mudança, não só quando a requisição sai: sem isso, nos 350 ms
    // do debounce o botão continuava liberado com a simulação da seleção anterior.
    useEffect(() => {
        if (!selected.length) {
            setPreview(null)
            setPreviewing(false)
            return
        }
        let cancelled = false
        setPreviewing(true)
        const timer = setTimeout(async () => {
            try {
                const response = await previewOrphanLinkMany(orphan.id, selected, anyThirdParty ? payer.trim() : undefined)
                if (cancelled) return
                if (response.success && response.result) setPreview(response.result)
                else {
                    // Simulação que falhou não pode deixar a anterior liberando o botão.
                    setPreview(null)
                    toast.error(response.error || 'Não foi possível conferir os atendimentos.')
                }
            } finally {
                if (!cancelled) setPreviewing(false)
            }
        }, 350)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [orphan.id, selected, payer, anyThirdParty])

    const add = (appointmentType: OrphanAppointmentType, appointmentId: number) => {
        if (selectedKeys.has(keyOf({ appointmentType, appointmentId }))) return
        setSelected(current => [...current, { appointmentType, appointmentId }])
    }

    const remove = (key: string) => setSelected(current => current.filter(a => keyOf(a) !== key))

    const toggleThirdParty = (key: string, value: boolean) =>
        setSelected(current => current.map(a => (keyOf(a) === key ? { ...a, thirdPartyCovered: value } : a)))

    const handleSearch = async (event: React.FormEvent) => {
        event.preventDefault()
        const number = parseInt(term.trim(), 10)
        if (!Number.isFinite(number) || number <= 0) return
        setSearching(true)
        setSearched(false)
        try {
            // Escopo da organização: o Pix cai numa filial e o atendimento pode estar em outra.
            const response = await searchByNumber(number, 'organization')
            const found = (response.success && response.result ? response.result.hits : []).filter(hit =>
                APPOINTMENT_TYPES.includes(hit.type as OrphanAppointmentType),
            )
            setHits(found)
        } catch {
            setHits([])
        } finally {
            setSearched(true)
            setSearching(false)
        }
    }

    const confirmLink = async () => {
        setLinking(true)
        try {
            const response = await linkOrphanMany(orphan.id, selected, anyThirdParty ? payer.trim() : undefined)
            if (response.success) {
                setConfirmOpen(false)
                onLinked(response.result?.message || 'Pagamento vinculado.')
            } else {
                toast.error(response.error || 'Não foi possível vincular.')
            }
        } finally {
            setLinking(false)
        }
    }

    const rowsByKey = new Map((preview?.rows ?? []).map(row => [keyOf(row), row]))
    const difference = preview?.difference ?? orphan.amount
    const canLink = !!preview?.canLink && !previewing

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-3xl flex flex-col gap-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-semibold text-lg">Vincular a vários atendimentos</h3>
                        <p className="text-sm text-base-content/60">
                            Pix de <span className="font-medium text-base-content">{formatCurrency(orphan.amount)}</span> recebido em{' '}
                            {formatDate(orphan.paidAt)}
                            {orphan.payerName ? ` · pago por ${orphan.payerName}` : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        disabled={linking}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </div>

                <p className="text-sm text-base-content/70">
                    Para quando um único Pix pagou atendimentos de várias pessoas. O Pix passa a ser a cobrança de todos, cada um
                    com a sua parte, e o repasse sai sozinho. Só entram atendimentos da mesma filial, sem cobrança em aberto e sem
                    nada recebido; se algum tiver cobrança pendente, cancele-a no atendimento antes.
                </p>

                <form onSubmit={handleSearch} className="form-control w-full">
                    <label className="label pb-1" htmlFor="link-many-number">
                        <span className="label-text font-medium">Adicionar pelo número do atendimento</span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="link-many-number"
                            type="number"
                            min={1}
                            autoFocus
                            className="input input-bordered flex-1"
                            placeholder="Ex.: 73397"
                            value={term}
                            onChange={e => setTerm(e.target.value)}
                        />
                        <button type="submit" className="btn btn-secondary gap-1" disabled={searching || !term.trim()}>
                            {searching ? <span className="loading loading-spinner loading-sm" /> : <MdSearch className="text-lg" />}
                            Buscar
                        </button>
                    </div>
                </form>

                {searched && !hits.length && !searching && (
                    <p className="text-sm text-base-content/60">Nenhum atendimento com esse número.</p>
                )}

                {hits.length > 0 && (
                    <div className="flex flex-col gap-2">
                        {hits.map(hit => {
                            const type = hit.type as OrphanAppointmentType
                            const already = selectedKeys.has(keyOf({ appointmentType: type, appointmentId: hit.id }))
                            return (
                                <div
                                    key={`${hit.type}-${hit.id}`}
                                    className="rounded-xl border border-base-300/60 p-3 flex flex-wrap items-center justify-between gap-3"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">
                                            {typeLabel[type]} #{hit.id} · {hit.title}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            {hit.clientName ? `${ClientLabel}: ${hit.clientName}` : ''}
                                            {hit.statusLabel ? ` · ${hit.statusLabel}` : ''}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost border border-base-300 gap-1"
                                        disabled={already}
                                        onClick={() => add(type, hit.id)}
                                    >
                                        <MdAdd className="text-base" />
                                        {already ? 'Adicionado' : 'Adicionar'}
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                )}

                {orphan.suggestions.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">Sugestões deste Pix</p>
                        <div className="flex flex-wrap gap-2">
                            {orphan.suggestions.map(s => {
                                const already = selectedKeys.has(keyOf(s))
                                return (
                                    <button
                                        key={keyOf(s)}
                                        type="button"
                                        className="btn btn-xs btn-ghost border border-base-300 gap-1 font-normal"
                                        disabled={already}
                                        onClick={() => add(s.appointmentType, s.appointmentId)}
                                    >
                                        <MdAdd />
                                        {typeLabel[s.appointmentType]} #{s.appointmentId}
                                        {s.clientName ? ` · ${s.clientName}` : ''} · {formatCurrency(s.remaining)}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                        Atendimentos escolhidos ({selected.length})
                    </p>
                    {!selected.length ? (
                        <p className="text-sm text-base-content/60">Nenhum ainda. Busque pelo número ou use as sugestões.</p>
                    ) : (
                        selected.map(item => {
                            const key = keyOf(item)
                            const row = rowsByKey.get(key)
                            return (
                                <div
                                    key={key}
                                    className={`rounded-xl border p-3 flex flex-col gap-2 ${
                                        row?.blockedReason ? 'border-error/50 bg-error/5' : 'border-base-300/60'
                                    }`}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-medium truncate">
                                                {typeLabel[item.appointmentType]} #{item.appointmentId}
                                                {row?.clientName ? ` · ${row.clientName}` : ''}
                                            </p>
                                            {row?.title && <p className="text-xs text-base-content/60 truncate">{row.title}</p>}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{row ? formatCurrency(row.amount) : '...'}</span>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs btn-circle"
                                                onClick={() => remove(key)}
                                                aria-label="Remover"
                                            >
                                                <MdDeleteOutline className="text-base" />
                                            </button>
                                        </div>
                                    </div>
                                    {row?.thirdPartyAllowed && (
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-secondary mt-0.5"
                                                checked={!!item.thirdPartyCovered}
                                                onChange={e => toggleThirdParty(key, e.target.checked)}
                                            />
                                            <span className="text-sm">
                                                Pago por terceiro
                                                <span className="block text-xs text-base-content/60">
                                                    O cupom {row.couponCode} ({formatCurrency(row.couponDiscount)}) continua registrado, e
                                                    o atendimento entra pelo valor antes dele.
                                                </span>
                                            </span>
                                        </label>
                                    )}
                                    {row?.blockedReason && (
                                        <p className="text-sm text-error flex items-start gap-1">
                                            <MdErrorOutline className="text-base shrink-0 mt-0.5" />
                                            {row.blockedReason}
                                        </p>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>

                {anyThirdParty && (
                    <div className="form-control w-full">
                        <label className="label pb-1" htmlFor="link-many-payer">
                            <span className="label-text font-medium">Quem pagou</span>
                        </label>
                        <input
                            id="link-many-payer"
                            className="input input-bordered w-full"
                            maxLength={200}
                            placeholder="Ex.: Empresa X, sorteio entre os funcionários"
                            value={payer}
                            onChange={e => setPayer(e.target.value)}
                        />
                        <label className="label pt-1">
                            <span className="label-text-alt text-base-content/60">Fica gravado na cobrança.</span>
                        </label>
                    </div>
                )}

                <div className="rounded-xl bg-base-200/60 p-4 grid grid-cols-3 gap-3 text-sm">
                    <div>
                        <p className="text-base-content/60">Pix</p>
                        <p className="font-semibold">{formatCurrency(orphan.amount)}</p>
                    </div>
                    <div>
                        <p className="text-base-content/60">Atendimentos</p>
                        <p className="font-semibold">{formatCurrency(preview?.total ?? 0)}</p>
                    </div>
                    <div>
                        <p className="text-base-content/60">Diferença</p>
                        <p className={`font-semibold ${difference !== 0 ? 'text-error' : 'text-success'}`}>
                            {formatCurrency(difference)}
                        </p>
                    </div>
                </div>

                {!!preview?.errors.length && (
                    <ul className="text-sm text-warning flex flex-col gap-1">
                        {preview.errors.map(error => (
                            <li key={error} className="flex items-start gap-1">
                                <MdErrorOutline className="text-base shrink-0 mt-0.5" />
                                {error}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="modal-action mt-0">
                    <button type="button" className="btn btn-ghost" onClick={onClose} disabled={linking}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-secondary" disabled={!canLink} onClick={() => setConfirmOpen(true)}>
                        {previewing && <span className="loading loading-spinner loading-xs" />}
                        {selected.length === 1 ? 'Vincular a 1 atendimento' : `Vincular aos ${selected.length} atendimentos`}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !linking && onClose()} />

            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmLink}
                loading={linking}
                title="Vincular a vários atendimentos"
                message={`O Pix de ${formatCurrency(orphan.amount)} passa a ser a cobrança de ${selected.length} atendimentos, cada um com a sua parte, e os repasses são gerados. Ele sai desta lista.`}
                confirmButtonText="Vincular"
                confirmButtonClass="btn-secondary"
            />
        </div>
    )
}
